// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Component } from "react";
import { connect } from "react-redux";

import PropTypes from "prop-types";

import { api, apiThatThrows } from "containers/Request";
import { withRouter } from "utils/decorators/withRouter";
import { camelCaseKeys } from "utils/object";

import { IsVersionUpdatedProvider } from "./_shared/IsVersionUpdated";
import { withVersions } from "./_shared/withVersionsDecorator";
import { VersionsTab } from "./VersionsTab";

@withRouter
@withVersions
@connect(null, (dispatch) => ({
  getCategoriesPaginated: () => {
    return dispatch(
      api.getApplicationCategoriesPaginated.action({
        queryParams: { limit: "nolimit" },
      })
    );
  },
  getOrganization: (organizationId) => {
    return dispatch(
      api.getOrganizationPublicInfo.action({
        params: { organizationId },
      })
    );
  },
  upgradeOrganizationPlan: (organizationId, organizationPlan) => {
    return dispatch(
      apiThatThrows.upgradeOrganizationPlan.action({
        params: { organizationId, organizationPlan },
      })
    );
  },
}))
export class VersionsTabData extends Component {
  static propTypes = {
    application: PropTypes.object.isRequired,

    // from @connect
    getCategoriesPaginated: PropTypes.func,
    getOrganization: PropTypes.func,
    upgradeOrganizationPlan: PropTypes.func,

    // from @withVersions
    refreshVersions: PropTypes.func.isRequired,
    versionsLoading: PropTypes.bool.isRequired,
    versionsError: PropTypes.string,
    activeVersion: PropTypes.object,
    latestVersion: PropTypes.object,
  };

  state = {
    loading: true,
    error: "",
    categories: [],
    appOwner: null,
  };

  async componentDidMount() {
    const { refreshVersions, application } = this.props;

    try {
      this.setState({ loading: true });
      const categories = await this.getCategories();

      await refreshVersions(application);

      const appOwner = await this.getAppOwner();

      this.setState({
        loading: false,
        categories,
        appOwner,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.toString(),
      });
    }
  }

  async getCategories() {
    const { error, result } = await this.props.getCategoriesPaginated();
    if (error) {
      throw new Error(error);
    }
    return result.results;
  }

  async getAppOwner() {
    const { getOrganization, application } = this.props;

    const { error, result } = await getOrganization(
      application.owner_public_id
    );
    if (error) {
      throw new Error(error);
    }

    return camelCaseKeys(result.results);
  }
  async upgradeOrganizationPlan(id, plan) {
    const { upgradeOrganizationPlan } = this.props;
    this.setState({ loading: true, error: "" });
    try {
      await upgradeOrganizationPlan(id, plan);
      const appOwner = await this.getAppOwner();
      this.setState({
        appOwner,
      });
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }
  render() {
    const {
      application,
      versionsLoading,
      versionsError,
      activeVersion,
      latestVersion,
    } = this.props;

    return (
      <IsVersionUpdatedProvider
        activeVersion={activeVersion}
        latestVersion={latestVersion}
      >
        <VersionsTab
          activeVersion={activeVersion}
          latestVersion={latestVersion}
          categories={this.state.categories}
          loading={this.state.loading || versionsLoading}
          error={this.state.error || versionsError}
          application={application}
          appOwner={this.state.appOwner}
          upgradeOrganizationPlan={async (id, plan) =>
            this.upgradeOrganizationPlan(id, plan)
          }
        />
      </IsVersionUpdatedProvider>
    );
  }
}
